import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CalendarDays, MapPin, Clock, Dribbble, ChevronRight,
  Users, Zap, Star, Heart, Home, Award, Crown, Loader2
} from "lucide-react";

const TIER_STYLES = {
  free: "bg-slate-100 text-slate-700",
  founding: "bg-amber-100 text-amber-800",
  silver: "bg-slate-200 text-slate-800",
  gold: "bg-yellow-100 text-yellow-800",
  platinum: "bg-purple-100 text-purple-800",
};

export default function PlayerDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/player/dashboard").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  const { profile, home_court, favorite_courts, upcoming_bookings, total_upcoming_bookings, upcoming_events, upcoming_sessions } = data;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="player-dashboard">
      {/* Profile Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl" data-testid="player-name">{profile.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs text-muted-foreground font-mono" data-testid="membership-id">{profile.membership_id}</span>
            <Badge className={TIER_STYLES[profile.membership_tier] || TIER_STYLES.free} data-testid="membership-tier">
              <Crown className="w-3 h-3 mr-1" />{profile.tier_info?.name || "Free"}
            </Badge>
            {profile.founding_flag && <Badge className="bg-amber-500 text-white text-[10px]"><Award className="w-3 h-3 mr-0.5" />Founder</Badge>}
            {profile.reliability_badge && <Badge variant="outline" className="text-[10px] capitalize">{profile.reliability_badge}</Badge>}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/profile")} className="text-xs">Edit Profile</Button>
      </div>

      {/* Preferences */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Preferences</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* My Sports */}
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Dribbble className="w-3 h-3" /> My Sports</p>
              <div className="flex flex-wrap gap-1">
                {(profile.sports || []).length > 0 ? profile.sports.map(s => (
                  <Badge key={s} variant="outline" className="text-[10px] capitalize">{s.replace("-", " ")}</Badge>
                )) : <span className="text-xs text-muted-foreground italic">Not set</span>}
              </div>
            </div>
            {/* Home Court */}
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Home className="w-3 h-3" /> My Home Court</p>
              {home_court ? (
                <button onClick={() => navigate(`/venues/${home_court.venue_id}`)} className="text-sm font-medium text-primary hover:underline truncate block">{home_court.name}</button>
              ) : <span className="text-xs text-muted-foreground italic">Not set</span>}
            </div>
            {/* Favorites */}
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Heart className="w-3 h-3" /> Favorite Courts</p>
              {favorite_courts.length > 0 ? favorite_courts.map(f => (
                <button key={f.venue_id} onClick={() => navigate(`/venues/${f.venue_id}`)} className="text-sm text-primary hover:underline truncate block">{f.name}</button>
              )) : <span className="text-xs text-muted-foreground italic">None yet</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <button onClick={() => navigate("/book-court")} data-testid="action-book-court"
          className="bg-primary/5 hover:bg-primary/10 border-2 border-primary/20 hover:border-primary/40 rounded-lg p-4 sm:p-6 text-center transition-all hover:-translate-y-1">
          <CalendarDays className="w-7 h-7 sm:w-8 sm:h-8 text-primary mx-auto mb-2" />
          <p className="font-heading text-xs sm:text-sm uppercase tracking-wide">Book a Court</p>
        </button>
        <button onClick={() => navigate("/build-team")} data-testid="action-build-team"
          className="bg-amber-50 hover:bg-amber-100 border-2 border-amber-200 hover:border-amber-300 rounded-lg p-4 sm:p-6 text-center transition-all hover:-translate-y-1">
          <Users className="w-7 h-7 sm:w-8 sm:h-8 text-amber-600 mx-auto mb-2" />
          <p className="font-heading text-xs sm:text-sm uppercase tracking-wide">Build Your Team</p>
        </button>
        <button onClick={() => navigate("/find-coach")} data-testid="action-book-coach"
          className="bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-300 rounded-lg p-4 sm:p-6 text-center transition-all hover:-translate-y-1">
          <Award className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 mx-auto mb-2" />
          <p className="font-heading text-xs sm:text-sm uppercase tracking-wide">Book a Coach</p>
        </button>
        <button onClick={() => navigate("/events")} data-testid="action-events"
          className="bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 hover:border-purple-300 rounded-lg p-4 sm:p-6 text-center transition-all hover:-translate-y-1">
          <Zap className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600 mx-auto mb-2" />
          <p className="font-heading text-xs sm:text-sm uppercase tracking-wide">Events</p>
        </button>
      </div>

      {/* My Bookings */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base" data-testid="my-bookings-title">My Bookings</CardTitle>
            {total_upcoming_bookings > 3 && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/bookings")} className="text-xs" data-testid="view-all-bookings">
                View All ({total_upcoming_bookings}) <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {upcoming_bookings.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No upcoming bookings</p>
              <Button variant="link" size="sm" onClick={() => navigate("/book-court")} className="mt-1">Book your first court</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {upcoming_bookings.map(b => (
                <div key={b.booking_id} className="flex items-center justify-between py-2 border-b last:border-0 hover:bg-muted/30 px-2 rounded cursor-pointer"
                  onClick={() => navigate(`/venues/${b.venue_id}`)} data-testid={`booking-${b.booking_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center shrink-0">
                      <CalendarDays className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{b.venue_name}</p>
                      <p className="text-xs text-muted-foreground">{b.start_dt?.slice(0, 10)} &middot; {b.start_dt?.slice(11, 16)} - {b.end_dt?.slice(11, 16)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="text-[10px] capitalize">{b.sport_type}</Badge>
                    <Badge className={b.status === "CONFIRMED" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"} variant="secondary">
                      {b.status === "CONFIRMED" ? "Confirmed" : b.status === "PENDING_CASH" ? "Pay at Venue" : b.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Events & Sessions Row */}
      {(upcoming_events.length > 0 || upcoming_sessions.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {upcoming_events.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Upcoming Events</CardTitle></CardHeader>
              <CardContent>
                {upcoming_events.map(e => (
                  <div key={e.event_id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-muted/30 rounded px-1"
                    onClick={() => navigate(`/events/${e.event_id}`)}>
                    <Zap className="w-4 h-4 text-purple-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm truncate">{e.title}</p>
                      <p className="text-xs text-muted-foreground">{e.event_date} {e.start_time}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {upcoming_sessions.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Upcoming Sessions</CardTitle></CardHeader>
              <CardContent>
                {upcoming_sessions.map(s => (
                  <div key={s.session_id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-muted/30 rounded px-1"
                    onClick={() => navigate(`/coach/sessions/${s.session_id}`)}>
                    <Star className="w-4 h-4 text-blue-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm truncate">{s.title}</p>
                      <p className="text-xs text-muted-foreground">{s.session_date} {s.start_time} · {s.venue_name}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
